import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import {
  getAllBlocksByProject,
  getEstimatedAmount,
  getPaymentTemplatesByProjectId,
  createPaymentTemplates,
  uploadFilesToS3,
  postUnitProjectImages
} from '../../services/api/implementation/impl';
import { useQuery, useMutation } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import AddPaymentTemplate from './AddPaymentTemplate';
import { ImagePlus, X } from 'lucide-react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { numberToWords } from '../../utils/numberToWords';

// Validation schema
const validationSchema = yup.object().shape({
  absolute: yup.number().required('Final Price is required'),
  gst_percent: yup.string().required('GST % is required'),
  lead_id: yup.number().required('Lead is required'),
  unit_id: yup.number().required('Unit is required'),
  paymentType: yup.string().required('Payment Type is required'),
  template_id: yup
    .number()
    .nullable()
    .when('paymentType', {
      is: 'Time Based',
      then: (schema) => schema.required('Payment Template is required'),
      otherwise: (schema) => schema.nullable()
    })
});

export const ClosedWonCloseDeal = ({
  onClose,
  onSubmit,
  isSubmitting,
  selectedLead,
  ucDetails,
  skipFiltering = false
}) => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [unitData, setUnitData] = useState();
  const [estimatedAmount, setEstimatedAmount] = useState('0');
  const [priceInWords, setPriceInWords] = useState('');
  // Optional extra charge IDs that are selected; mandatory (from ucIncludedExtraCharges or extra_charges_arr) are disabled
  const [selectedOptionalExtraIds, setSelectedOptionalExtraIds] = useState(new Set());
  const [isOpenTemplateModal, setIsOpenTemplateModal] = useState(false);
  const [isSubmittingTemplate, setIsSubmittingTemplate] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageVisibility, setImageVisibility] = useState('private'); // 'private' | 'public'
  const imageInputRef = React.useRef(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      absolute: '',
      gst_percent: '',
      lead_id: Number(selectedLead),
      project: ucDetails?.project,
      block: ucDetails?.block,
      level: ucDetails?.level,
      unit_id: ucDetails?.unit,
      paymentType: '',
      template_id: null
    }
  });

  const paymentType = watch('paymentType');

  const projectID = watch('project');

  // Track page view
  useEffect(() => {
    mixpanel.track('Closed Won Close Deal Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: AllBlocks, refetch: fetchBlocks } = useQuery({
    queryKey: ['Allblocks'],
    queryFn: () => getAllBlocksByProject(projectID)
  });

  React.useEffect(() => {
    fetchBlocks();
  }, [projectID]);

  // Fetch payment templates for the selected project
  const { data: paymentTemplates, refetch: refetchPaymentTemplates } = useQuery({
    queryKey: ['payment_templates', projectID],
    queryFn: () => getPaymentTemplatesByProjectId(projectID),
    enabled: !!projectID,
    onError: (error) => {
      console.error('Error fetching payment templates:', error);
    }
  });

  // Mutation for creating payment template
  const { mutate: handleCreateTemplate } = useMutation({
    mutationFn: (payload) => createPaymentTemplates(payload),
    onMutate: () => {
      setIsSubmittingTemplate(true);
    },
    onSuccess: () => {
      setIsSubmittingTemplate(false);
      toast.success('Payment template created successfully');
      setIsOpenTemplateModal(false);
      refetchPaymentTemplates();
    },
    onError: (error) => {
      setIsSubmittingTemplate(false);
      console.error('Error creating payment template:', error);
      toast.error(
        error?.response?.data?.message || error?.message || 'Error creating payment template'
      );
    }
  });

  // Transform payment templates for dropdown
  const paymentTemplateOptions =
    paymentTemplates?.map((template) => ({
      label: template.templateName,
      id: template.id,
      value: template.id
    })) || [];

  const transformedDataBlocks =
    AllBlocks?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id,
      level: item.levels || []
    })).sort((a, b) => a.label.localeCompare(b.label)) || [];

  const [levels, setLevels] = useState([]);
  const BlockChange = (levels) => {
    const formattedLevels = levels
      ?.map((level) => ({
        label: level.name || '',
        id: level.id,
        value: level.id,
        units: level.units || []
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setLevels(formattedLevels);
  };

  const [units, setUnits] = useState([]);
  const LevelChange = (units) => {
    const availableUnits = skipFiltering
      ? units
      : (units || []).filter(
          (unit) =>
            unit.status === 'Available' ||
            unit.status === 'VACANT' ||
            unit.status === 'vacant' ||
            unit.status === 'Temporary_Booked' ||
            unit.status === 'Vacant' //only units with status Available or Vacant
        );
    const formattedUnits = availableUnits
      ?.map((unit) => ({
        label: unit.unitName || '',
        id: unit.id,
        value: unit.id,
        data: unit || {}
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setUnits(formattedUnits);
  };

  // Set lead_id in form state when selectedLead changes
  React.useEffect(() => {
    if (selectedLead && selectedLead.id) {
      setValue('lead_id', selectedLead.id);
    }
  }, [selectedLead, setValue]);

  // When unit changes: init extra charge checkboxes from ucIncludedExtraCharges (if present) or all optional from extra_charges_arr
  React.useEffect(() => {
    const arr = unitData?.extra_charges_arr;
    if (!Array.isArray(arr)) {
      setSelectedOptionalExtraIds(new Set());
      return;
    }
    const ucIncluded = unitData?.underConsideration?.[0]?.ucIncludedExtraCharges ?? [];
    if (ucIncluded.length > 0) {
      const ucByName = {};
      ucIncluded.forEach((uc) => {
        ucByName[uc.name] = uc.is_mandatory === true || uc.is_mandatory === 'true';
      });
      const optionalSelected = arr
        .filter((ec) => {
          const ucMandatory = ucByName[ec.name];
          return ucMandatory === false;
        })
        .map((ec) => ec.id);
      setSelectedOptionalExtraIds(new Set(optionalSelected));
    } else {
      const optionalIds = arr
        .filter((ec) => ec.isMandatory !== true && ec.isMandatory !== 'true')
        .map((ec) => ec.id);
      setSelectedOptionalExtraIds(new Set(optionalIds));
    }
  }, [unitData?.id, unitData?.extra_charges_arr, unitData?.underConsideration]);

  // Watch for changes in the absolute (Final Price) field and convert to words
  const finalPrice = watch('absolute');
  React.useEffect(() => {
    if (finalPrice && Number(finalPrice) > 0) {
      setPriceInWords(numberToWords(Number(finalPrice)));
    } else {
      setPriceInWords('');
    }
  }, [finalPrice]);

  // Prefill form fields when ucDetails is available - single comprehensive effect
  React.useEffect(() => {
    if (!ucDetails || !projects || !AllBlocks) return;

    const prefillForm = async () => {
      try {
        // Step 1: Set project
        if (ucDetails.project) {
          const projectExists = projects.some((project) => project.id === ucDetails.project);
          if (projectExists) {
            setValue('project', ucDetails.project);
            // Wait for project to be set and blocks to load
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Step 2: Set block and load levels
        if (ucDetails.block && AllBlocks.length > 0) {
          const selectedBlock = AllBlocks.find((block) => block.id === ucDetails.block);
          if (selectedBlock && selectedBlock.levels) {
            BlockChange(selectedBlock.levels);
            setValue('block', ucDetails.block);
            // Wait for levels to be loaded
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Step 3: Set level and load units
        if (ucDetails.level && levels.length > 0) {
          const selectedLevel = levels.find((level) => level.id === ucDetails.level);
          if (selectedLevel && selectedLevel.units) {
            LevelChange(selectedLevel.units);
            setValue('level', ucDetails.level);
            // Wait for units to be loaded
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Step 4: Set unit and unit data
        if (ucDetails.unit && units.length > 0) {
          const selectedUnit = units.find((unit) => unit.id === ucDetails.unit);
          if (selectedUnit && selectedUnit.data) {
            setUnitData(selectedUnit.data);
            setValue('unit', ucDetails.unit);
            setValue('unit_id', ucDetails.unit);
          }
        }
      } catch (error) {
        console.error('Error prefilling form:', error);
      }
    };

    prefillForm();
  }, [ucDetails, projects, AllBlocks, levels, units, setValue]);

  // Handle cascading effect when project changes (for manual selection)
  React.useEffect(() => {
    if (projectID && AllBlocks && projectID !== ucDetails?.project) {
      // Reset form when project changes manually
      setValue('block', '');
      setValue('level', '');
      setValue('unit', '');
      setLevels([]);
      setUnits([]);
      setUnitData(undefined);
    }
  }, [projectID, AllBlocks, ucDetails?.project, setValue]);

  const onSubmitForm = async (data) => {
    const { absolute, gst_percent, ...rest } = data;
    const payload = {
      ...rest,
      lead_id: Number(data.lead_id),
      unit_id: Number(data.unit_id),
      total_price: parseFloat(absolute),
      gst_percentage: parseInt(gst_percent),
      paymentType: data.paymentType
    };

    if (data.paymentType === 'Time Based' && data.template_id) {
      payload.template_id = Number(data.template_id);
    }

    // included_extra_charge_ids = mandatory (from ucIncludedExtraCharges or extra_charges_arr) + optional checked
    const extraArr = unitData?.extra_charges_arr ?? [];
    const ucIncluded = unitData?.underConsideration?.[0]?.ucIncludedExtraCharges ?? [];
    const ucMandatoryByName = {};
    ucIncluded.forEach((uc) => {
      ucMandatoryByName[uc.name] = uc.is_mandatory === true || uc.is_mandatory === 'true';
    });
    const mandatoryIds = extraArr
      .filter((ec) => {
        if (ucIncluded.length > 0) {
          const fromUC = ucMandatoryByName[ec.name];
          return fromUC === true;
        }
        return ec.isMandatory === true || ec.isMandatory === 'true';
      })
      .map((ec) => ec.id);
    const optionalSelectedIds = Array.from(selectedOptionalExtraIds);
    const included_extra_charge_ids = [...mandatoryIds, ...optionalSelectedIds];
    if (included_extra_charge_ids.length > 0) {
      payload.included_extra_charge_ids = included_extra_charge_ids;
    }

    try {
      // 1. Upload images to S3 and then register via projectImages API (if any)
      if (imageFiles.length > 0 && projectID) {
        const blockId = watch('block');
        const unitId = watch('unit_id');
        if (!blockId) {
          toast.error('Select a block before adding images.');
          return;
        }
        setIsUploadingImages(true);
        const uploadedFiles = await uploadFilesToS3(imageFiles);
        if (uploadedFiles?.length > 0) {
          const imagesPayload = {
            images: uploadedFiles.map((f) => ({
              name: f.name,
              url: f.url,
              project_id: Number(projectID),
              block_id: Number(blockId),
              ...(unitId && { unit_id: Number(unitId) }),
              visibility: imageVisibility
            }))
          };
          console.log("imagesPayload:",imagesPayload);
          await postUnitProjectImages(imagesPayload);
          toast.success(`${uploadedFiles.length} image(s) added to project.`);
          setImageFiles([]);
        }
      } else if (imageFiles.length > 0 && !projectID) {
        toast.error('Select a project before adding images.');
        return;
      }

      // 2. Submit close-deal payload
      onSubmit?.(payload);
      console.log("projectImages:");
      console.log("Closed Won Close Deal Payload:",payload);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload images or close deal');
      console.error('ClosedWonCloseDeal submit error:', err);
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleImageFilesChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    const valid = files.filter((f) => imageTypes.includes(f.type));
    if (valid.length < files.length) toast.error('Only image files (JPEG, PNG, GIF, WebP, BMP) are allowed.');
    setImageFiles((prev) => [...prev, ...valid]);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const removeImageFile = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  React.useEffect(() => {
    console.log(errors);
  }, [errors]);

  // Debounce timer for API calls
  const [debounceTimer, setDebounceTimer] = useState(null);

  const handleTotalAmountInput = async (e) => {
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Validate required fields
    if (e === '' || watch('gst_percent') === '' || watch('unit_id') === '') {
      setEstimatedAmount(null);
      return;
    }

    // Set new timer to call API after 500ms of no typing
    const timer = setTimeout(async () => {
      try {
        console.log('Calling API with value:', e);
        const response = await getEstimatedAmount({
          lead_id: Number(watch('lead_id')),
          unit_id: Number(watch('unit_id')),
          gst_percentage: parseInt(watch('gst_percent')),
          total_price: e
        });
        setValue('closed_gst', `₹${parseInt(response.gst_price).toLocaleString('en-IN')}`);
        setValue('closed_unit', `₹${parseInt(response.unit_price).toLocaleString('en-IN')}`);
        setValue('closed_plc', `₹${parseInt(response.plc_price).toLocaleString('en-IN')}`);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.message || 'Error getting estimated amount'
        );
        console.log('API Error:', error);
      }
    }, 500); // 500ms delay

    setDebounceTimer(timer);
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Close Deal</span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="grid grid-cols-2 gap-4">
          {/* Project Name */}
          <FormInput
            width={100}
            label={'Project'}
            type={'dropdown'}
            readOnly={skipFiltering}
            register={register}
            register_key={'project'}
            options={[
              { label: 'Select Project', value: '', disabled: true, selected: true },
              ...projectsForDropdown
            ]}
          />

          {/* Block Name */}
          <FormInput
            width={100}
            label={'Block'}
            type={'dropdown'}
            readOnly={skipFiltering}
            register={register}
            register_key={'block'}
            options={[
              { label: 'Select Block', value: '', disabled: true, selected: true },
              ...transformedDataBlocks
            ]}
            allvalues={(value) => {
              BlockChange(value.level);
              setValue('level', '');
              setValue('unit', '');
              setUnits([]);
              setUnitData(undefined);
            }}
          />

          {/* Level */}
          <FormInput
            width={100}
            label={'Level'}
            type={'dropdown'}
            readOnly={skipFiltering || levels?.length === 0}
            register={register}
            register_key={'level'}
            options={[
              { label: 'Select Level', value: '', disabled: true, selected: true },
              ...levels
            ]}
            allvalues={(value) => {
              LevelChange(value.units);
              setValue('unit', '');
              setUnitData(undefined);
            }}
          />

          {/* Unit */}
          <FormInput
            width={100}
            label={'Unit'}
            type={'dropdown'}
            readOnly={skipFiltering || units?.length === 0}
            register={register}
            register_key={'unit'}
            options={[
              { label: 'Select Unit', value: '', disabled: true, selected: true },
              ...units
            ]}
            allvalues={(value) => {
              setUnitData(value.data);
              setValue('unit_id', value.id); // set unit_id in form state
            }}
          />

          {/* Unit Price */}
          <FormInput
            width={100}
            label={'Unit Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'unit_price'}
            valuePre={unitData?.cost ? `₹${parseInt(unitData.cost).toLocaleString('en-IN')}` : ''}
          />

          <FormInput
            width={100}
            label={'GST'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'gst'}
            valuePre={unitData?.gst ? `₹${parseInt(unitData.gst).toLocaleString('en-IN')}` : ''}
          />

          {/* {unitData?.extra_charges !== '0' && ( */}
          <FormInput
            width={100}
            label={'Extra Charges'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'extra_charges'}
            valuePre={
              unitData?.extra_charges
                ? `₹${parseInt(unitData.extra_charges).toLocaleString('en-IN')}`
                : ''
            }
          />
          {/* )} */}

          {/* Extra charges: from ucIncludedExtraCharges, is_mandatory true = disabled + checked; optional = enabled */}
          {Array.isArray(unitData?.extra_charges_arr) && unitData.extra_charges_arr.length > 0 && (
            <div className="col-span-2 flex flex-col gap-2">
              <label className="font-medium">Extra Charges</label>
              <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded border border-gray-200">
                {(() => {
                  const ucIncluded = unitData?.underConsideration?.[0]?.ucIncludedExtraCharges ?? [];
                  const ucMandatoryByName = {};
                  ucIncluded.forEach((uc) => {
                    ucMandatoryByName[uc.name] =
                      uc.is_mandatory === true || uc.is_mandatory === 'true';
                  });
                  return unitData.extra_charges_arr.map((ec) => {
                    const fromUCMandatory = ucIncluded.length > 0 ? ucMandatoryByName[ec.name] : null;
                    const isMandatory =
                      fromUCMandatory !== undefined
                        ? fromUCMandatory
                        : ec.isMandatory === true || ec.isMandatory === 'true';
                    const isChecked =
                      isMandatory || selectedOptionalExtraIds.has(ec.id);
                    const isDisabled = isMandatory;
                    return (
                      <label
                        key={ec.id}
                        className={`flex items-center gap-2 ${
                          isDisabled ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={() => {
                            if (isDisabled) return;
                            setSelectedOptionalExtraIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(ec.id)) next.delete(ec.id);
                              else next.add(ec.id);
                              return next;
                            });
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">
                          {ec.name || 'Extra charge'}
                          {ec.total_price != null && (() => {
                            const base = Number(ec.total_price) || 0;
                            const gstPct = Number(ec.gst) || 0;
                            const gstAmount = (base * gstPct) / 100;
                            const totalWithGst = base + gstAmount;
                            return ` — ₹${totalWithGst.toLocaleString('en-IN', { maximumFractionDigits: 0 })} (₹${base.toLocaleString('en-IN')} + ₹${gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} GST)`;
                          })()}
                          {isMandatory && (
                            <span className="ml-1 text-gray-500">(Required)</span>
                          )}
                        </span>
                      </label>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          <FormInput
            width={100}
            label={'Plc Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'plc_price'}
            valuePre={
              unitData?.plc_price ? `₹${parseInt(unitData.plc_price).toLocaleString('en-IN')}` : ''
            }
          />

          <FormInput
            width={100}
            label={'Total Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'cost'}
            valuePre={`₹${(
              (parseInt(unitData?.cost) || 0) +
              (parseInt(unitData?.plc_price) || 0) +
              (parseInt(unitData?.gst) || 0)
            ).toLocaleString('en-IN')}.00`}
          />

          {/* GST % Radio Buttons */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium">GST %</label>
            <div className="flex gap-4">
              <label>
                <input type="radio" value="0" {...register('gst_percent')} />
                0%
              </label>
              <label>
                <input type="radio" value="1" {...register('gst_percent')} />
                1%
              </label>
              <label>
                <input type="radio" value="5" {...register('gst_percent')} />
                5%
              </label>
            </div>
            {errors.gst_percent && (
              <span className="text-red-500 text-xs">{errors.gst_percent.message}</span>
            )}
          </div>

          {/* Unit Price */}
          <FormInput
            width={100}
            label={'Close Deal Unit Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'closed_unit'}
            valuePre={`₹0`}
          />

          <FormInput
            width={100}
            label={'Close Deal GST'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'closed_gst'}
            valuePre={`₹0`}
          />

          <FormInput
            width={100}
            label={'Close Deal Plc Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'closed_plc'}
            valuePre={`₹0`}
          />

          {/* Payment Type */}
          <FormInput
            width={100}
            label={'Payment Type'}
            type={'dropdown'}
            register={register}
            register_key={'paymentType'}
            errors={errors.paymentType}
            options={[
              { label: 'Select Payment Type', value: '', disabled: true, selected: true },
              { label: 'Time Based', value: 'Time Based' },
              { label: 'Construction Based', value: 'Construction Based' }
            ]}
            allvalues={(value) => {
              // Reset template_id when payment type changes
              setValue('template_id', null);
            }}
          />

          {/* Payment Template - only show if Time Based is selected */}
          {paymentType === 'Time Based' && (
            <div className="flex relative">
              <FormInput
                width={100}
                label={'Payment Template'}
                type={'dropdown'}
                register={register}
                register_key={'template_id'}
                errors={errors.template_id}
                options={[
                  { label: 'Select Template', value: '', disabled: true, selected: true },
                  ...paymentTemplateOptions
                ]}
              />
              <div className="absolute right-0 top-0 ">
                <button
                  type="button"
                  className="text-sm text-blue-500 underline"
                  onClick={() => {
                    if (!projectID) {
                      toast.error('Please select a project first');
                      return;
                    }
                    setIsOpenTemplateModal(true);
                  }}>
                  Add New
                </button>
              </div>
            </div>
          )}

          {/* Multiple images: upload to S3 then projectImages API */}
          <div className="col-span-2 flex flex-col gap-2">
            <label className="font-medium">Images (optional)</label>
            <div className="flex items-center gap-4 mb-1">
              <span className="text-sm text-stone-600">Image visibility:</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="imageVisibility"
                  value="private"
                  checked={imageVisibility === 'private'}
                  onChange={() => setImageVisibility('private')}
                  className="text-blue-600"
                />
                <span className="text-sm">Private</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="imageVisibility"
                  value="public"
                  checked={imageVisibility === 'public'}
                  onChange={() => setImageVisibility('public')}
                  className="text-blue-600"
                />
                <span className="text-sm">Public</span>
              </label>
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
              multiple
              className="hidden"
              onChange={handleImageFilesChange}
            />
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
            >
              <ImagePlus size={18} />
              Add images
            </button>
            {imageFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {imageFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-2 py-1.5 text-sm"
                  >
                    <span className="truncate max-w-[140px]" title={file.name}>{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeImageFile(index)}
                      className="p-0.5 rounded hover:bg-stone-200 text-stone-500"
                      aria-label="Remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Discount (absolute) - moved to last */}
          <div className="flex flex-col">
            <FormInput
              width={100}
              label={'Final Price'}
              register={register}
              register_key={'absolute'}
              type={'number'}
              errors={errors.absolute}
              onType={(value) => {
                handleTotalAmountInput(value);
                const numValue = Number(value);
                if (numValue && numValue > 0) {
                  setPriceInWords(numberToWords(numValue));
                } else {
                  setPriceInWords('');
                }
              }}
            />
            {priceInWords && (
              <span className="text-sm text-gray-600 mt-1 italic">{priceInWords}</span>
            )}
          </div>

          {/* Hidden fields */}
          <input type="hidden" {...register('lead_id')} />
          <input type="hidden" {...register('unit_id')} />
        </div>

        <div className="flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || isUploadingImages}>
            {isUploadingImages ? 'Uploading images...' : 'Close Deal'}
          </Button>
        </div>
      </form>

      {/* Add Payment Template Modal */}
      {isOpenTemplateModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          name={'Create Payment Template'}
          width={55}
          onClose={() => {
            setIsOpenTemplateModal(false);
          }}>
          <AddPaymentTemplate
            isSubmitting={isSubmittingTemplate}
            projectId={projectID}
            handleOnSubmit={handleCreateTemplate}
            onClose={() => {
              setIsOpenTemplateModal(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};
